import { Component, OnDestroy, OnInit } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { CookieConsentService } from "../../../Services/cookie-consent.service";

/**
 * URL prefixes where the consent banner should hide itself so the user can
 * actually read the related content before making a choice. The choice is
 * NOT persisted while the banner is hidden — it returns as soon as the user
 * navigates away.
 */
const HIDE_ON_ROUTES = ["/cookie-policy"];

@Component({
  selector: "app-cookie-consent",
  templateUrl: "./cookie-consent.component.html",
  styleUrls: ["./cookie-consent.component.scss"],
})
export class CookieConsentComponent implements OnInit, OnDestroy {
  isVisible = false;

  private subscriptions = new Subscription();

  constructor(
    private cookieConsent: CookieConsentService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.cookieConsent.visibility$.subscribe((visible) => {
        this.isVisible = visible;
      })
    );

    // First load: only show the banner if the user is NOT currently on a
    // route where we want it hidden (e.g. the cookie policy page itself).
    if (!this.shouldHideForUrl(this.router.url)) {
      this.cookieConsent.initialize();
    }

    // Listen for route changes so the banner hides while the user reads the
    // cookie policy and reappears afterwards (until they save a choice).
    this.subscriptions.add(
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          if (this.cookieConsent.hasConsented()) {
            return;
          }

          if (this.shouldHideForUrl(event.urlAfterRedirects)) {
            this.cookieConsent.close();
          } else {
            this.cookieConsent.openSettings();
          }
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  acceptAll(): void {
    this.cookieConsent.acceptAll();
  }

  acceptMandatoryOnly(): void {
    this.cookieConsent.acceptMandatoryOnly();
  }

  private shouldHideForUrl(url: string): boolean {
    if (!url) {
      return false;
    }
    const path = url.split("?")[0].split("#")[0];
    return HIDE_ON_ROUTES.some((prefix) => path.startsWith(prefix));
  }
}
